import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ME_QUERY_KEY } from './session';

/**
 * SSE change stream: an audited mutation on the server invalidates the
 * queries it can have changed (debounced), so concurrent coordinators see each
 * other's edits without polling (SPEC §4.7).
 */
export function useChangeStream(enabled: boolean) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const source = new EventSource('/api/v1/stream');
    let timer: ReturnType<typeof setTimeout> | null = null;
    let tags = false;
    let postLabels = false;
    let people = false;
    // What a batch of changes asks to refetch. Mail and the classifier change
    // who waits on whom and the threads, not the plan, so they spare the board.
    let plan = false;
    let comms = false;
    let dnevnik = false;
    let triage = false;
    let allThreads = false;
    const threadsOf = new Set<string>();

    source.addEventListener('change', (e: MessageEvent<string>) => {
      const change = parseChange(e.data);
      // A tag edit or delete, and a label put on or taken off a post, change
      // the post list without touching any post's `updatedAt`. Any other post
      // write is left out on purpose: refetching it would remount the open
      // Mesta editor over its unsaved edits.
      if (change?.entity === 'tag') tags = true;
      if (change?.entity === 'post' && change.kind === 'tags-updated') postLabels = true;
      if (change?.entity === 'coordinator') {
        // A login granted or renamed changes the names every screen shows —
        // this login's own among them, and the dialog that asks for it.
        people = true;
        dnevnik = true;
      } else if (change?.entity === 'message') {
        // A mail read in, a label set or read by the model. Only a mail in or
        // out of triage changes the triage list; the classifier writes no Dnevnik.
        comms = true;
        allThreads = true;
        if (change.kind === 'triage' || change.kind === 'assigned') triage = true;
        if (change.kind !== 'classified') dnevnik = true;
      } else {
        plan = true;
        // An answer on the page is that person's thread; a problem resolved on
        // Live names the problem, not the person.
        if (change?.entity === 'volunteer' && change.entityId) threadsOf.add(change.entityId);
        // A problem resolved on Live, and a shift change that decides whether a
        // briefing still waits, change what the open thread says.
        if (change?.entity === 'problem' || change?.entity === 'shift') allThreads = true;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (tags) void qc.invalidateQueries({ queryKey: ['tags'] });
        if (tags || postLabels) void qc.invalidateQueries({ queryKey: ['posts'] });
        if (people) {
          void qc.invalidateQueries({ queryKey: ['coordinators'] });
          void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
          // The send card names who sent each mail by the name they have now.
          void qc.invalidateQueries({ queryKey: ['mail'] });
        }
        if (triage) void qc.invalidateQueries({ queryKey: ['messages', 'triage'] });
        if (allThreads) {
          void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'messages' && q.queryKey[1] !== 'triage' });
        } else {
          for (const id of threadsOf) void qc.invalidateQueries({ queryKey: ['messages', id] });
        }
        if (plan || comms) {
          void qc.invalidateQueries({ queryKey: ['volunteers'] });
          void qc.invalidateQueries({ queryKey: ['overview'] });
        }
        if (plan || dnevnik) void qc.invalidateQueries({ queryKey: ['changelog'] });
        if (plan) {
          void qc.invalidateQueries({ queryKey: ['timeline'] });
          void qc.invalidateQueries({ queryKey: ['problems'] });
          // The roster rollups and the Ne pride card read shift rows and the
          // cancel record, which another coordinator's cancel changes too.
          void qc.invalidateQueries({ queryKey: ['shifts'] });
          void qc.invalidateQueries({ queryKey: ['pending'] });
          void qc.invalidateQueries({ queryKey: ['cancel-plan'] });
          void qc.invalidateQueries({ queryKey: ['notes'] });
        }
        tags = false;
        postLabels = false;
        people = false;
        plan = false;
        comms = false;
        dnevnik = false;
        triage = false;
        allThreads = false;
        threadsOf.clear();
      }, 300);
    });

    // The server says `access` and ends the stream when this login's access
    // changes on Uporabniki (SPEC §1.1L); EventSource then reconnects under the
    // new role, or gives up for good on a 401. Asking who we are again picks up
    // the new capabilities or sends the layout to /login. A dropped connection
    // it is still retrying asks nothing: only one it has given up on does.
    source.addEventListener('access', () => {
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    });
    source.addEventListener('error', () => {
      if (source.readyState === EventSource.CLOSED) void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    });

    return () => {
      if (timer) clearTimeout(timer);
      source.close();
    };
  }, [enabled, qc]);
}

function parseChange(data: string): { entity: string; kind: string; entityId: string | null } | null {
  try {
    return JSON.parse(data) as { entity: string; kind: string; entityId: string | null };
  } catch {
    return null;
  }
}
